import { create } from 'zustand';
import type { Device } from '../types/room';
import type { AcMode, PowerState } from '../lib/device/catalog';
import { POWER_STATE } from '../lib/device/catalog';
import { mockDevices } from '../data/mockDevices';

interface RoomStore {
  // Room Info
  roomNumber: string;
  guestName: string;

  // Devices
  devices: Device[];

  // Mood Presets
  activePreset: string | null;

  // Connection (mock)
  isConnected: boolean;

  // Actions
  toggleDevice: (deviceId: string) => void;
  setBrightness: (deviceId: string, brightness: number) => void;
  setTemperature: (deviceId: string, temperature: number) => void;
  setACMode: (deviceId: string, mode: AcMode) => void;
  setCurtainPosition: (deviceId: string, position: number) => void;
  applyMoodPreset: (presetId: string) => void;
  setActivePreset: (presetId: string | null) => void;
}

/** Patch one device by id, leaving the rest of the list untouched. */
const patchDevice = (devices: Device[], deviceId: string, patch: Partial<Device>): Device[] =>
  devices.map((device) => (device.id === deviceId ? { ...device, ...patch } : device));

const flipPowerState = (status: PowerState): PowerState =>
  status === POWER_STATE.ON ? POWER_STATE.OFF : POWER_STATE.ON;

export const useRoomStore = create<RoomStore>((set) => ({
  roomNumber: '101',
  guestName: 'Guest',

  // Demo devices; replaced by the RCU configuration once a technician defines one.
  devices: mockDevices,

  activePreset: null,
  isConnected: true,

  toggleDevice: (deviceId) =>
    set(({ devices }) => ({
      devices: devices.map((device) =>
        device.id === deviceId ? { ...device, status: flipPowerState(device.status) } : device,
      ),
      // Changing a device by hand means the room is no longer on a preset.
      activePreset: null,
    })),

  setBrightness: (deviceId, brightness) =>
    set(({ devices }) => ({ devices: patchDevice(devices, deviceId, { brightness }) })),

  setTemperature: (deviceId, temperature) =>
    set(({ devices }) => ({ devices: patchDevice(devices, deviceId, { targetTemp: temperature }) })),

  setACMode: (deviceId, mode) =>
    set(({ devices }) => ({ devices: patchDevice(devices, deviceId, { mode }) })),

  setCurtainPosition: (deviceId, position) =>
    set(({ devices }) => ({ devices: patchDevice(devices, deviceId, { position }) })),

  applyMoodPreset: (presetId) => {
    // Presets are display-only today; applying one will fan out commands later.
    set({ activePreset: presetId });
  },

  setActivePreset: (presetId) => set({ activePreset: presetId }),
}));

export default useRoomStore;
